import React, { useState, useEffect } from "react";
import { adminApiFetch, normalizeMenus } from "./api";

function Menus() {
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL;
    const [menus, setMenus] = useState([]);
    const [editedMenus, setEditedMenus] = useState([]);
    const [newMenus, setNewMenus] = useState([]);
    const [errorMsg, setErrorMsg] = useState("");
    const [deletedMenuIds, setDeletedMenuIds] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const categoryOptions = [
        "breakfast_packages",
        "breakfast",
        "drinks",
        "fruits",
        "extras"
    ];

    useEffect(() => {
        adminApiFetch(`${baseUrl}/menus`)
            .then(res => res.json())
            .then(data => {
                const normalizedMenus = normalizeMenus(data);
                setMenus(normalizedMenus);
                setEditedMenus(normalizedMenus.map(m => ({ ...m })));
            })
            .catch(() => setMenus([]));
    }, [baseUrl]);

    const handleChange = (idx, field, value) => {
        const updated = [...editedMenus];
        updated[idx][field] = value;
        setEditedMenus(updated);
    };

    const handleNewMenuChange = (idx, field, value) => {
        const updated = [...newMenus];
        updated[idx][field] = value;
        setNewMenus(updated);
    };

    const handleAddNewMenuRow = () => {
        setNewMenus([
            ...newMenus,
            {
                name: "", description: "", price: "",
                category: "", ingredients: "", image_path: ""
            }
        ]);
    };

    const isMenuChanged = (original, edited) => {
        const fields = [
            "name", "description", "price", "category", "ingredients", "image_path"
        ];
        return fields.some(field => (original[field] || "") !== (edited[field] || ""));
    };

    const isAnyRequiredFieldEmpty = () => {
        for (const menu of editedMenus) {
            if (
                !menu.name ||
                !menu.description ||
                menu.price === "" || menu.price == null ||
                !menu.category ||
                !menu.image_path
            ) return true;
        }
        for (const menu of newMenus) {
            if (
                !menu.name ||
                !menu.description ||
                menu.price === "" || menu.price == null ||
                !menu.category ||
                !menu.image_path
            ) return true;
        }
        return false;
    };

    const prepareMenus = menusArr =>
        menusArr.map(menu => ({
            ...menu,
            price: menu.price === "" || menu.price == null ? 0 : Number(menu.price)
        }));

    const handleSave = async () => {
        if (isSaving) return;
        setErrorMsg("");
        const changedMenus = editedMenus.filter((edited, idx) => {
            const original = menus[idx];
            return isMenuChanged(original, edited);
        });
        const changedMenusForSave = changedMenus.filter(m => !deletedMenuIds.includes(m.id));
        const menusToDelete = menus.filter(m => deletedMenuIds.includes(m.id));

        const handleResponse = async (res, defaultError) => {
            if (res.ok) return res.status === 204 ? null : res.json();
            let err;
            try {
                err = await res.json();
            } catch {
                throw new Error(defaultError);
            }
            throw new Error(err.detail?.[0]?.msg || err.detail || defaultError);
        };

        try {
            setIsSaving(true);

            if (menusToDelete.length > 0) {
                const deleteRes = await adminApiFetch(`${baseUrl}/menus`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(menusToDelete)
                });
                await handleResponse(deleteRes, "Error while deleting");
            }

            if (changedMenusForSave.length > 0) {
                const updateRes = await adminApiFetch(`${baseUrl}/menus`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(prepareMenus(changedMenusForSave))
                });
                await handleResponse(updateRes, "Error while updating");
            }

            if (newMenus.length > 0) {
                const createRes = await adminApiFetch(`${baseUrl}/menus`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(prepareMenus(newMenus))
                });
                await handleResponse(createRes, "Error while creating");
            }

            const reload = await adminApiFetch(`${baseUrl}/menus`);
            const data = await reload.json();
            const normalizedMenus = normalizeMenus(data);
            setMenus(normalizedMenus);
            setEditedMenus(normalizedMenus.map(m => ({ ...m })));
            setNewMenus([]);
            setDeletedMenuIds([]);
        } catch (err) {
            setErrorMsg(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveNewMenuRow = (idx) => {
        setNewMenus(newMenus.filter((_, i) => i !== idx));
    };

    const handleMarkMenuForDelete = (id) => {
        if (deletedMenuIds.includes(id)) {
            setDeletedMenuIds(deletedMenuIds.filter(delId => delId !== id));
        } else {
            setDeletedMenuIds([...deletedMenuIds, id]);
        }
    };

    const changedCount = editedMenus.reduce((count, menu, idx) => {
        const original = menus[idx];
        if (!original || deletedMenuIds.includes(menu.id)) return count;
        return isMenuChanged(original, menu) ? count + 1 : count;
    }, 0);

    const pendingCount = changedCount + newMenus.length + deletedMenuIds.length;

    return (
        <div className="management-page">
            <div className="management-page-header">
                <h2 className="management-page-title">Menu Dashboard</h2>
                <div className="management-badge-row">
                    <span className="management-badge management-badge-neutral">Total: {editedMenus.length}</span>
                    <span className="management-badge management-badge-new">New: {newMenus.length}</span>
                    <span className="management-badge management-badge-changed">Changed: {changedCount}</span>
                    <span className="management-badge management-badge-delete">Delete: {deletedMenuIds.length}</span>
                    <span className="management-badge management-badge-neutral">Pending: {pendingCount}</span>
                </div>
            </div>

            <div className="management-callout">
                The fields Category, Name, Description, Price and Image Path are required. Changes are applied after clicking "Save all Changes".
            </div>

            {errorMsg && (
                <div className="management-error">{errorMsg}</div>
            )}

            <div className="management-table-shell">
            <table className="management-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Name*</th>
                        <th>Description*</th>
                        <th>Price*</th>
                        <th>Category*</th>
                        <th>Ingredients</th>
                        <th>Image Path*</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {editedMenus.map((menu, idx) => {
                        const isDeleted = deletedMenuIds.includes(menu.id);
                        const isChanged = !isDeleted && isMenuChanged(menus[idx], menu);
                        const rowClass = isDeleted
                            ? "management-table-row-delete"
                            : (isChanged ? "management-table-row-changed" : "");

                        return (
                        <tr key={menu.id} className={rowClass}>
                            <td>
                                <div className="management-row-id">
                                    <span>{menu.id}</span>
                                    {isChanged && <span className="management-badge management-badge-changed">changed</span>}
                                    {isDeleted && <span className="management-badge management-badge-delete">delete</span>}
                                </div>
                            </td>
                            <td>
                                <input name="name" value={menu.name || ""} onChange={e => handleChange(idx, "name", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input name="description" value={menu.description || ""} onChange={e => handleChange(idx, "description", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input
                                    name="price"
                                    type="number"
                                    value={menu.price === 0 ? "0" : (menu.price || "")}
                                    onChange={e => handleChange(idx, "price", e.target.value)}
                                    disabled={isDeleted}
                                />                            </td>
                            <td>
                                <select
                                    name="category"
                                    value={menu.category || ""}
                                    onChange={e => handleChange(idx, "category", e.target.value)}
                                    disabled={isDeleted}
                                >
                                    <option value="">Choose Category</option>
                                    {categoryOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <input name="ingredients" value={menu.ingredients || ""} onChange={e => handleChange(idx, "ingredients", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input name="image_path" value={menu.image_path || ""} onChange={e => handleChange(idx, "image_path", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <button
                                    type="button"
                                    onClick={() => handleMarkMenuForDelete(menu.id)}
                                    className="management-action-btn"
                                    aria-label="Delete menu / row"
                                    title={isDeleted ? "Undo delete menu / row" : "Delete menu / row"}
                                >
                                    {isDeleted ? "↺" : "✖"}
                                </button>
                            </td>
                        </tr>
                    );})}
                    {newMenus.map((menu, idx) => (
                        <tr key={`new-${idx}`} className="management-table-row-new">
                            <td>
                                <div className="management-row-id">
                                    <span>new</span>
                                    <span className="management-badge management-badge-new">new</span>
                                </div>
                            </td>
                            <td>
                                <input name="name" value={menu.name} onChange={e => handleNewMenuChange(idx, "name", e.target.value)} />
                            </td>
                            <td>
                                <input name="description" value={menu.description} onChange={e => handleNewMenuChange(idx, "description", e.target.value)} />
                            </td>
                            <td>
                                <input
                                    name="price"
                                    type="number"
                                    value={menu.price === 0 ? "0" : (menu.price || "")}
                                    onChange={e => handleNewMenuChange(idx, "price", e.target.value)}
                                />
                            </td>
                            <td>
                                <select
                                    name="category"
                                    value={menu.category || ""}
                                    onChange={e => handleNewMenuChange(idx, "category", e.target.value)}
                                >
                                    <option value="">Choose Category</option>
                                    {categoryOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <input name="ingredients" value={menu.ingredients} onChange={e => handleNewMenuChange(idx, "ingredients", e.target.value)} />
                            </td>
                            <td>
                                <input name="image_path" value={menu.image_path} onChange={e => handleNewMenuChange(idx, "image_path", e.target.value)} />
                            </td>
                            <td>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveNewMenuRow(idx)}
                                    className="management-action-btn"
                                    aria-label="Delete menu / row"
                                    title="Delete menu / row"
                                >
                                    &#10006;
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>

            <div className="management-action-bar">
            <button
                onClick={handleAddNewMenuRow}
                disabled={isAnyRequiredFieldEmpty() || isSaving}
            >
                Create new Menu
            </button>
            <button
                onClick={handleSave}
                disabled={isAnyRequiredFieldEmpty() || isSaving}
            >
                {isSaving ? "Saving..." : "Save all Changes"}
            </button>
            </div>
        </div>
    );
}

export default Menus;